/**
 * useAppConfig Hook
 *
 * Hook to load and access app configuration
 * Can be used without authentication (for login page)
 */

import { useEffect, useCallback } from 'react';
import { useAppConfigStore, appConfigSelectors } from '@/shared/stores/app-config.store';
import { GetAppConfigUseCase } from '@features/app-config/application/use-cases/GetAppConfigUseCase';
import { SupabaseAppConfigRepository } from '@features/app-config/infrastructure/repositories/SupabaseAppConfigRepository';

export function useAppConfig() {
  const store = useAppConfigStore();
  const config = useAppConfigStore(appConfigSelectors.selectConfig);
  const appName = useAppConfigStore(appConfigSelectors.selectAppName);
  const appTagline = useAppConfigStore(appConfigSelectors.selectAppTagline);
  // Light mode logos
  const logoSmall = useAppConfigStore(appConfigSelectors.selectLogoSmall);
  const logoMedium = useAppConfigStore(appConfigSelectors.selectLogoMedium);
  const logoLarge = useAppConfigStore(appConfigSelectors.selectLogoLarge);
  const logoFavicon = useAppConfigStore(appConfigSelectors.selectLogoFavicon);
  // Dark mode logos
  const logoDarkSmall = useAppConfigStore(appConfigSelectors.selectLogoDarkSmall);
  const logoDarkMedium = useAppConfigStore(appConfigSelectors.selectLogoDarkMedium);
  const logoDarkLarge = useAppConfigStore(appConfigSelectors.selectLogoDarkLarge);
  const logoDarkFavicon = useAppConfigStore(appConfigSelectors.selectLogoDarkFavicon);
  const primaryColor = useAppConfigStore(appConfigSelectors.selectPrimaryColor);
  const isLoaded = useAppConfigStore(appConfigSelectors.selectIsLoaded);
  const isLoading = useAppConfigStore(appConfigSelectors.selectIsLoading);
  const error = useAppConfigStore(appConfigSelectors.selectError);

  const loadConfig = useCallback(async () => {
    // Skip if already loading or loaded
    if (store.isLoading || store.isLoaded) return;

    store.setLoading(true);
    try {
      const repository = new SupabaseAppConfigRepository();
      const useCase = new GetAppConfigUseCase(repository);
      const configDTO = await useCase.execute();
      store.setConfig(configDTO);
    } catch (err) {
      console.error('Failed to load app config:', err);
      store.setError(err instanceof Error ? err.message : 'Error al cargar configuracion');
    }
  }, [store]);

  // Auto-load on mount
  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  return {
    // Full config object
    config,

    // Individual values
    appName,
    appTagline,
    // Light mode logos
    logoSmall,
    logoMedium,
    logoLarge,
    logoFavicon,
    // Dark mode logos
    logoDarkSmall,
    logoDarkMedium,
    logoDarkLarge,
    logoDarkFavicon,
    primaryColor,

    // State
    isLoaded,
    isLoading,
    error,

    // Actions
    reload: loadConfig,
  };
}
